// src/pages/OnboardingPage.tsx
'use client'
import React, { useState, ReactElement, useEffect, useMemo } from 'react';
import { ArrowLeft, ArrowRight, Calendar, Check, ReceiptText, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSupabase } from '../context/SupabaseContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { baseParent, baseStudent, baseTeacher, Student, Teacher } from '../components/interfaces/All';

interface OnboardingStep {
    title: string;
    description: string;
    icon: ReactElement;
    optional: boolean;
    image: string;
    role_exclusivity: string|undefined;
}

const STEPS: OnboardingStep[] = [
    {
      title: "Welcome to Kite",
      description: "Connect with expert teachers and track your learning journey with our all-in-one platform.",
      icon: <Star size={40} className="text-indigo-500 mb-6" />,
      optional: false,
      image: "",
      role_exclusivity: undefined,
    },
    {
      title: "Role selection",
      description: "",
      icon: <></>,
      optional: false,
      image: "",
      role_exclusivity: undefined,
    },
    {
      title: "Add your students",
      description: "Start by adding your students to the student list. They will be able to log in and book lessons with you.",
      icon: <></>,
      optional: true,
      image: "",
      role_exclusivity: 'teacher'
    },
    {
      title: "Lessons",
      description: "You can see your next appointments!",
      icon: <Calendar size={40} className="text-indigo-500 mb-6" />,
      optional: true,
      image: "",
      role_exclusivity: undefined,
    },
    {
      title: "Wallet",
      description: "Follow your financials in the wallet. Students can log in and see the state of their payments, and you can track who owes you what.",
      icon: <ReceiptText size={40} className="text-indigo-500 mb-6" />,
      optional: true,
      image: "",
      role_exclusivity: undefined,
    },
    {
      title: "You're All Set!",
      description: "Your account is ready! Start adding students.",
      icon: <Check size={40} className="text-indigo-500 mb-6" />,
      optional: false,
      image: "",
      role_exclusivity: undefined,
    }
];

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const { state, fetchUserRole, createTeacher, fetchTeachers, connectStudentAccountwithAuthId, connectParentAccountwithAuthId } = useSupabase();
  const {authUser} = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedRole, setSelectedRole] = useState<'teacher' | 'student' | 'parent' | null>(null);
  const [linkedStudents, setLinkedStudents] = useState<Student[] | null>(null);
  const [linkedTeachers, setLinkedTeachers] = useState<Teacher[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);

  useMemo(()=>{
    // set the role if its found in the db
    if(!state.userProfile||!state.userProfile.id||state.userProfile.id=='') return;
    setSelectedRole(state.userProfile.role);
    console.log(state.userProfile)
    fetchTeachers();
  },[])

  const finishAction = async() => {
    assignRole();
    navigate('/dashboard');
  }

  // finishing action - create/modify
  const assignRole = async () => {
    if (!selectedRole) return;
    if (!authUser) return;

    setIsSubmitting(true);

    try {
      const fullname = authUser.user_metadata.full_name ? authUser.user_metadata.full_name : 'Firstname Lastname';
      const firstname = fullname.split(' ').slice(0, -1).join(' ');
      const lastname = fullname.split(' ').pop();
      const phone = authUser.phone? authUser.phone : '';

      if (selectedRole == 'teacher' ){
        createTeacher({
          ...baseTeacher,
          auth_id: authUser.id,
          email:  authUser.email || '',
          phone: phone,
          first_name: firstname,
          last_name: lastname,
          picture: authUser.user_metadata.picture,
          auth_provider: authUser.user_metadata.iss,
          status: "active",
          description: "",
          expiration_date: new Date(),
          notes: `{email_verified: ${authUser.user_metadata.email_verified}},`,
          } as Teacher
        )
      } else if(selectedRole == 'student' ){
        if(!authUser.email) {console.error('email not defined'); return; }
        if(!authUser.id) {console.error('auth_id not defined'); return; }
        connectStudentAccountwithAuthId({
            auth_id: authUser.id,
            email:  authUser.email,
          }
        )
        console.log("connected to teacher", state.userProfile.teacher_id, state.teachers )
      } else if(selectedRole == 'parent' ){
        if(!authUser.email) {console.error('email not defined'); return;}
        if(!authUser.id) {console.error('auth_id not defined'); return; }
        connectParentAccountwithAuthId({
            auth_id: authUser.id,
            email:  authUser.email,
          }
        )
        console.log("connected to teacher", state.userProfile.teacher_id, state.teachers )
      }
    } catch (error: any) {
        console.error('Role assignment failed:', error);
        alert('Failed to assign role. Please try again.');
    } finally {
        setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    const nextStep = currentStep + 1;
    if (currentStep === 1 && !selectedRole) {
      alert('Please select a role');
      return;
    }
    if (currentStep < STEPS.length - 1) {
      if (!STEPS[nextStep].role_exclusivity || STEPS[nextStep].role_exclusivity == selectedRole ) {
        setCurrentStep(nextStep);
      }else {
        setCurrentStep(nextStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  if (onboardingCompleted) return null;

  return (
    <div className=" bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-4xl mx-auto"
      >
        {/* Progress Dots */}
        <div className="flex justify-center mb-8 gap-2">
          {STEPS.map((_, index) => (
            <motion.div
              key={index}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index <= currentStep ? 'bg-indigo-600 scale-125' : 'bg-gray-300'
              }`}
              animate={{ scale: index === currentStep ? 1.25 : 1 }}
              transition={{ type: "spring", stiffness: 300 }}
            />
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                {STEPS[currentStep].icon}
                <h1 className="text-5xl lg:text-6xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-6">
                  {STEPS[currentStep].title}
                </h1>
              </div>
              <p className="text-xl text-gray-700 leading-relaxed max-w-lg mx-auto lg:mx-0">
                {STEPS[currentStep].description}
              </p>
            </div>

            {/* Step 2: Role Selection */}
            {/* TODO: incomming users are either teachers or students */}
            {/* 1. They are teachers - they select teacher option */}
            {/* 2. They are parent/student - backend finds for reference to their email */}
            {/* 2.1. Teacher is found -> displayed, users confirm */}
            {/* 2.2. Teacher is not found -> their email was not found, they need to log in using different email or ask teacher to use this email */}
            {/* ! the search for their email account should happen automatically and not ask for role selection */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-4"
              >
              {!selectedRole && (<>
                <p className="text-xl text-gray-700 leading-relaxed max-w-lg mx-auto lg:mx-0">
                  Select your role.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { role: 'teacher' as const, title: 'Teacher', desc: 'Manage students, schedule lessons, track payments', icon: '👨‍🏫' },
                    // { role: 'student' as const, title: 'Student', desc: 'Book lessons, track progress, manage payments', icon: '🎓' },
                    // { role: 'parent' as const, title: 'Parent', desc: 'Monitor child progress, manage payments, communicate', icon: '👨‍👩‍👧' }
                  ].map(({ role, title, desc, icon }) => (
                    <motion.button
                      key={role}
                      onClick={() => setSelectedRole(role)}
                      className={`p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center gap-3 h-full ${
                        selectedRole === role
                          ? 'border-indigo-500 bg-indigo-50 shadow-xl transform scale-105'
                          : 'border-gray-200 hover:border-indigo-300 hover:shadow-lg'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="text-4xl">{icon}</div>
                      <h3 className="font-bold text-xl">{title}</h3>
                      <p className="text-gray-600 text-sm text-center">{desc}</p>
                      {selectedRole === role && (
                        <Check className="w-6 h-6 text-indigo-500" />
                      )}
                    </motion.button>
                  ))}
                </div>
              </>)}

              {/* In case DB already contains a profile */}
              {selectedRole && (<>
                <p className="text-xl text-gray-700 leading-relaxed max-w-lg mx-auto lg:mx-0">
                  We found your profile, please confirm by tapping Next.
                </p>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="text-sm text-blue-800">
                    
                    {selectedRole=='student' && (<>
                      Your teacher is:
                      <ul className="list-disc pt-3 pb-3">
                        {state.teachers.map((t)=>(
                          <li key={t.id} className="flex items-left gap-3 ps-2">
                            <img className="rounded-xl" width={20} height={20} alt="teacherprofile picture" src={t.picture} />
                            <div>
                              {t.first_name} {t.last_name} - {t.email}
                            </div>
                          </li>
                        ))} 
                      </ul>
                      is this correct? Your role is set as {state.userRole}.
                    </>)}
                    
                    {selectedRole=='parent' && (<>
                      You are connected with teacher 
                      <ul className="list-disc pt-3 pb-3">
                        {state.teachers.map((t)=>(
                          <li key={t.id} className="flex items-left gap-3 ps-2">
                            <img className="rounded-xl" width={20} height={20} alt="teacherprofile picture" src={t.picture} />
                            <div>
                              {t.first_name} {t.last_name} - {t.email}
                            </div>
                          </li>
                        ))} 
                      </ul>
                      and students 
                      <ul className="list-disc pt-3 pb-3">
                        {state.students.map((t)=>(
                          <li key={t.id} className="flex items-left gap-3 ps-2">
                            <img className="rounded-xl" width={20} height={20} alt="teacherprofile picture" src={t.picture} />
                            <div>
                              {t.first_name} {t.last_name} - {t.email}
                            </div>
                          </li>
                        ))} 
                      </ul>
                      is this correct? Your role is set as {state.userRole}.
                    </>)}

                    {selectedRole=='teacher' && (<>
                      Your profile is set to teacher.
                    </>)}

                  </div>
                </div>
              
              </>)}

              </motion.div>
            )}

            {/* Navigation */}
            <div className="flex gap-4 pt-4">
              <motion.button
                onClick={prevStep}
                disabled={currentStep === 0}
                className="flex-1 h-12 px-6 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowLeft size={20} />
                Back
              </motion.button>

              <motion.button
                onClick={currentStep === STEPS.length - 1 ? finishAction : nextStep}
                disabled={
                  isSubmitting ||
                  (currentStep === 1 && !selectedRole) ||
                  (currentStep === STEPS.length - 1 && isSubmitting)
                }
                className="flex-1 h-12 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  'Completing...'
                ) : currentStep === STEPS.length - 1 ? (
                  <>
                    Finish <Check size={20} />
                  </>
                ) : (
                  'Next'
                )}
                {currentStep !== STEPS.length - 1 && <ArrowRight size={20} />}
              </motion.button>
            </div>
          </motion.div>

          {/* Right Side - Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="hidden lg:block"
          >
            <div className="w-full h-[500px] bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-3xl p-12 flex items-center justify-center">
              <div className="text-8xl animate-bounce">🎓</div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Onboarding;
